import { MEALS, MEAL_WHY } from "./lunelle-data";

// Faiths where astrology is usually not part of practice: zodiac starts hidden for members, with a switch to show it
export const ZODIAC_OFF_BY_DEFAULT = ["christian", "muslim", "jewish"];
export const zodiacVisible = (profile) => profile?.show_zodiac ?? !ZODIAC_OFF_BY_DEFAULT.includes(profile?.faith ?? "spiritual");

export const DIET = {
  christian: { label: "No food rules", note: "No food rules all year. In Lent, many skip meat on Fridays.", grace: "Say grace and give thanks for your daily bread." },
  muslim: { label: "Halal", note: "Every plate is halal: halal-certified meat, no pork and no alcohol, cooking wine included.", grace: "Begin with Bismillah and eat in moderation, leaving room to breathe." },
  jewish: { label: "Kosher-friendly", note: "Kosher-friendly: no shellfish or pork, and meat is never mixed with dairy at the same meal.", grace: "Pause for a bracha before you eat." },
  hindu: { label: "Vegetarian", note: "Vegetarian plates with no meat, fish or eggs. Dairy like paneer and ghee is welcome.", grace: "Offer your food with gratitude first, as prasad." },
  buddhist: { label: "Plant-forward", note: "Plant-forward plates, in the spirit of non-harm.", grace: "Eat slowly and mindfully, grateful for every hand that grew this." },
  spiritual: { label: "Open table", note: "No food rules. Eat what feels nourishing to you.", grace: "Pause, breathe and thank your body before you eat." },
};

// Faith versions of every plate: same dish style, following each faith's food rules
const HALAL = {
  menstrual: { ecto: "Braised halal grass-fed beef ragù (no wine) over creamy polenta with garlicky organic greens and shaved parmesan." },
  follicular: {
    ecto: "Crispy halal chicken shawarma bowl with saffron rice, whipped garlic toum, cucumber and pickled turnips.",
    meso: "Seared halal chicken with lemon-herb orzo, blistered tomatoes and a whipped feta drizzle.",
  },
  ovulatory: { endo: "15-minute Thai basil halal chicken lettuce wraps with crunchy organic veggies, cashews and lime." },
  luteal: { ecto: "Smoky halal turkey chili loaded with black beans and sweet potato, topped with avocado and a dollop of Greek yogurt." },
};
const KOSHER = {
  menstrual: { ecto: "Braised kosher beef ragù over olive-oil polenta with garlicky organic greens and toasted pine nuts." },
  follicular: {
    ecto: "Crispy kosher chicken shawarma bowl with saffron rice, garlic toum, cucumber and pickled turnips.",
    meso: "Seared kosher chicken with lemon-herb orzo, blistered tomatoes and a silky tahini drizzle.",
    endo: "10-minute kimchi fried rice (kosher-certified kimchi) with organic veggies, a jammy egg and toasted sesame.",
  },
  ovulatory: {
    ecto: "Crispy wild-caught fish tacos on corn tortillas with charred broccoli slaw, chipotle-lime crema and pickled onions.",
    endo: "15-minute Thai basil kosher chicken lettuce wraps with crunchy organic veggies, cashews and a soy-lime glaze.",
  },
  luteal: { ecto: "Smoky kosher turkey chili loaded with black beans and sweet potato, topped with avocado and a cashew-lime cream." },
};
const VEGETARIAN = {
  menstrual: {
    ecto: "Slow-braised lentil & mushroom ragù over creamy polenta with garlicky organic greens and vegetarian parmesan.",
    meso: "Miso-glazed paneer with sesame-ginger soba, charred bok choy and a chili-crisp drizzle.",
  },
  follicular: {
    ecto: "Crispy paneer tikka shawarma bowl with saffron rice, mint raita, cucumber and pickled turnips.",
    meso: "Seared halloumi with lemon-herb orzo, blistered tomatoes and a whipped feta drizzle.",
    endo: "10-minute vegetarian kimchi fried rice with organic veggies, crispy tofu and toasted sesame.",
  },
  ovulatory: {
    ecto: "Chipotle cauliflower tacos on corn tortillas with charred broccoli slaw, lime crema and pickled onions.",
    meso: "Crispy tofu poke bowl with mango, edamame, avocado, brown rice and an eggless sriracha mayo.",
    endo: "15-minute Thai basil tofu lettuce wraps with crunchy organic veggies, cashews and lime.",
  },
  luteal: {
    ecto: "Smoky three-bean chili with sweet potato, topped with avocado and a dollop of Greek yogurt.",
    endo: "Sheet-pan tandoori paneer with roasted butternut squash, broccolini and a pumpkin-seed crunch.",
  },
};
const PLANT = {
  menstrual: {
    ecto: "Slow-braised lentil & mushroom ragù over olive-oil polenta with garlicky organic greens and toasted pine nuts.",
    meso: "Miso-glazed tofu with sesame-ginger soba, charred bok choy and a chili-crisp drizzle.",
    endo: "15-minute coconut red lentil curry with organic spinach, ginger and warm whole-wheat roti.",
  },
  follicular: {
    ecto: "Crispy chickpea shawarma bowl with saffron rice, garlic-tahini sauce, cucumber and pickled turnips.",
    meso: "Seared tempeh with lemon-herb orzo, blistered tomatoes and a silky tahini drizzle.",
    endo: "10-minute vegan kimchi fried rice with organic veggies, crispy tofu and toasted sesame.",
  },
  ovulatory: {
    ecto: "Chipotle cauliflower tacos on corn tortillas with charred broccoli slaw, cashew-lime crema and pickled onions.",
    meso: "Crispy tofu poke bowl with mango, edamame, avocado, brown rice and a vegan sriracha mayo.",
    endo: "15-minute Thai basil tofu lettuce wraps with crunchy organic veggies, cashews and lime.",
  },
  luteal: {
    ecto: "Smoky three-bean chili with sweet potato, topped with avocado and a cashew-lime cream.",
    meso: "Chickpea tikka masala in a silky coconut-tomato sauce with brown basmati and warm roti.",
    endo: "Sheet-pan miso tempeh with roasted butternut squash, broccolini and a pumpkin-seed crunch.",
  },
};
export const SWAPS = { muslim: HALAL, jewish: KOSHER, hindu: VEGETARIAN, buddhist: PLANT };

export const mealFor = (phase, body, faith) => SWAPS[faith]?.[phase]?.[body] ?? MEALS[phase][body];
export const whyFor = (phase, body) => MEAL_WHY[phase][body];

// Fasting seasons & holy days, 2026-2027. Lunar dates can shift a day by region or moon sighting.
const RAMADAN = ["On your period days you're exempt from fasting. Rest, eat iron-rich meals and make up the days later if you choose. At iftar, break your fast gently with dates and water.",
  "Your period falls in Ramadan: you're exempt from fasting and the daily prayers on these days. Rest, eat iron-rich suhoor-style meals, and keep your connection through dhikr and dua."];
const LENT = ["Meat-free Fridays: on period days, choose lentils, salmon or beans to keep your iron up.",
  "Fasting falls on your period: a gentler Lent is still Lent. Give up a treat instead of a meal, and keep your iron up with lentils and greens."];
const YOM_KIPPUR = ["A 25-hour fast. Hydrate well and eat a slow, complex-carb meal beforehand.",
  "The fast falls on your period: hydrate well for two days before, eat iron-rich food at the meal before, and speak to your rabbi or doctor if cramps or heavy flow make fasting unsafe."];
const NAVRATRI = ["Vrat-friendly plates: sabudana, kuttu, fruit and dairy.",
  "Navratri on your period: many women adapt or pause the vrat on these days. Follow your family's custom and your body's needs, and break the fast gently with fruit and milk."];
const VASSA = ["A season of mindful restraint. Keep meals simple and nourishing.",
  "Your inner winter meets the Rains Retreat: slow down, rest more and let meditation be gentle."];
const HOLY = (tip, periodTip) => [tip, periodTip];
const EID = HOLY("A day of celebration and gratitude. Enjoy the feast and time with family.",
  "Eid on your period: celebrate fully. Take it easy on rich food if you feel bloated, and pair sweets with protein.");
const FEAST = HOLY("A day to celebrate, gather and give thanks.",
  "A holy day on your period: plan rest between gatherings, keep a warm drink close and pace the rich food.");

const E = (name, start, end, kind, [tip, periodTip]) => ({ name, start, end, kind, tip, periodTip });

export const FAITH_DAYS = {
  muslim: [
    E("Ramadan", "2026-02-18", "2026-03-19", "fast", RAMADAN), E("Eid al-Fitr", "2026-03-20", "2026-03-20", "holy", EID),
    E("Day of Arafah", "2026-05-26", "2026-05-26", "fast", HOLY("A voluntary fast many keep before Eid al-Adha.", "Arafah on your period: you're exempt from the fast. Spend the day in dhikr and dua instead.")),
    E("Eid al-Adha", "2026-05-27", "2026-05-27", "holy", EID),
    E("Ramadan", "2027-02-08", "2027-03-08", "fast", RAMADAN), E("Eid al-Fitr", "2027-03-09", "2027-03-09", "holy", EID),
    E("Eid al-Adha", "2027-05-16", "2027-05-16", "holy", EID),
  ],
  christian: [
    E("Lent", "2026-02-18", "2026-04-04", "fast", LENT), E("Good Friday", "2026-04-03", "2026-04-03", "fast", LENT), E("Easter", "2026-04-05", "2026-04-05", "holy", FEAST),
    E("Christmas", "2026-12-25", "2026-12-25", "holy", FEAST),
    E("Lent", "2027-02-10", "2027-03-27", "fast", LENT), E("Good Friday", "2027-03-26", "2027-03-26", "fast", LENT), E("Easter", "2027-03-28", "2027-03-28", "holy", FEAST),
    E("Christmas", "2027-12-25", "2027-12-25", "holy", FEAST),
  ],
  jewish: [
    E("Passover", "2026-04-02", "2026-04-09", "holy", HOLY("Eight days without chametz: lean on matzah, eggs, potatoes, fish and greens.", "Passover on your period: matzah can slow digestion, so add vegetables, fruit and water to every meal.")),
    E("Rosh Hashanah", "2026-09-12", "2026-09-13", "holy", FEAST),
    E("Yom Kippur", "2026-09-20", "2026-09-21", "fast", YOM_KIPPUR),
    E("Hanukkah", "2026-12-05", "2026-12-12", "holy", FEAST),
    E("Passover", "2027-04-22", "2027-04-29", "holy", HOLY("Eight days without chametz: lean on matzah, eggs, potatoes, fish and greens.", "Passover on your period: matzah can slow digestion, so add vegetables, fruit and water to every meal.")),
    E("Rosh Hashanah", "2027-10-02", "2027-10-03", "holy", FEAST),
    E("Yom Kippur", "2027-10-10", "2027-10-11", "fast", YOM_KIPPUR),
    E("Hanukkah", "2027-12-25", "2028-01-01", "holy", FEAST),
  ],
  hindu: [
    E("Holi", "2026-03-04", "2026-03-04", "holy", FEAST), E("Sharad Navratri", "2026-10-11", "2026-10-20", "fast", NAVRATRI),
    E("Diwali", "2026-11-08", "2026-11-08", "holy", FEAST),
    E("Holi", "2027-03-22", "2027-03-22", "holy", FEAST), E("Sharad Navratri", "2027-10-07", "2027-10-15", "fast", NAVRATRI),
    E("Diwali", "2027-10-29", "2027-10-29", "holy", FEAST),
  ],
  buddhist: [
    E("Vesak", "2026-05-31", "2026-05-31", "holy", HOLY("Buddha Day: generosity, gentle food and quiet reflection. Some traditions mark it on 1 May.", "Vesak on your period: let your practice be restful, with simple food and an early night.")),
    E("Vassa (Rains Retreat)", "2026-07-30", "2026-10-26", "fast", VASSA),
    E("Vesak", "2027-05-20", "2027-05-20", "holy", HOLY("Buddha Day: generosity, gentle food and quiet reflection. Dates vary by tradition.", "Vesak on your period: let your practice be restful, with simple food and an early night.")),
  ],
};

const day = (iso) => new Date(`${iso}T00:00:00`);

// Faith days covering a yyyy-MM-dd date (single days listed before long seasons)
export const faithDaysOn = (faith, iso) => (FAITH_DAYS[faith] ?? [])
  .filter((e) => iso >= e.start && iso <= e.end)
  .sort((a, b) => (a.start === a.end ? -1 : 0) - (b.start === b.end ? -1 : 0));

// Current fasting season, or the next one within 45 days
export const seasonFor = (faith, now = new Date()) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (const ev of (FAITH_DAYS[faith] ?? []).filter((e) => e.kind === "fast" && e.start !== e.end)) {
    const s = day(ev.start);
    const e = day(ev.end);
    if (today >= s && today <= e) return { name: ev.name, start: s, end: e, active: true, tip: ev.tip };
    const until = Math.round((s - today) / 86400000);
    if (until > 0 && until <= 45) return { name: ev.name, start: s, end: e, active: false, daysUntil: until, tip: ev.tip };
  }
  return null;
};
